'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';
import { MultistepForm } from '../../../../../form-controls/MultistepForm/MultistepForm';
import { useStepTracking } from '../../../../hooks/useStepTracking';
import { HiddenTrigger, MyAccountSheet } from '../../../../MyAccountSheet/MyAccountSheet';
import { BackHandler } from '../components/steps/BackHandler/BackHandler';
import { ConfirmationStep } from '../components/steps/ConfirmationStep/ConfirmationStep';
import { ReasonStep } from '../components/steps/ReasonStep/ReasonStep';
import { useCloseAccount } from '../hooks/UseCloseAccount';

type CloseAccountSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

const CloseAccountSheet = ({ open, onOpenChange }: CloseAccountSheetProps) => {
  const t = useTranslations('my_account');
  const [isFirstStep, setIsFirstStep] = useState(true);
  const { handleBackNavigation, registerBackHandler } = useCloseAccount();

  const formSchema = z.object({
    reason: z.string().min(1, t('pages.close_my_account.reason_step.validation')),
    additionalComments: z.string().optional(),
    confirmClosure: z.boolean().refine(val => val === true, {
      message: t('pages.close_my_account.confirmation_step.validation'),
    }),
  });

  type FormData = z.infer<typeof formSchema>;

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    mode: 'onBlur',
    defaultValues: {
      reason: '',
      additionalComments: '',
      confirmClosure: false,
    },
  });

  const steps = [
    {
      id: 'reason',
      component: (
        <>
          <BackHandler onBack={registerBackHandler} />
          <ReasonStep />
        </>
      ),
      schema: formSchema.pick({ reason: true, additionalComments: true }),
      'data-track-container': 'Close Reason',
    },
    {
      id: 'confirmation',
      component: (
        <>
          <BackHandler onBack={registerBackHandler} />
          <ConfirmationStep />
        </>
      ),
      schema: formSchema.pick({ confirmClosure: true }),
      'data-track-container': 'Close Confirm',
    },
  ];

  const { currentTrackContainer, onStepChange } = useStepTracking(steps);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      handleClose();
    }
  };

  const handleClose = () => {
    form.reset();
    setIsFirstStep(true);
    onOpenChange(false);
  };

  const handleComplete = () => {
    try {
      form.getValues();
      handleClose();
    } catch {
      handleClose();
    }
  };

  const handleBackClick = () => {
    handleBackNavigation(handleClose);
  };

  const handleStepChange = (isFirstStep: boolean, isLastStep: boolean, stepId: string) => {
    onStepChange(isFirstStep, isLastStep, stepId);
    setIsFirstStep(isFirstStep);
  };

  return (
    <MyAccountSheet
      trigger={<HiddenTrigger label={t('pages.close_my_account.title')} />}
      title={t('pages.close_my_account.title')}
      open={open}
      onOpenChange={handleOpenChange}
      showBackButton={!isFirstStep}
      onBack={handleBackClick}
      data-track-container={currentTrackContainer}
      data-track-feature="My Account"
    >
      <FormProvider {...form}>
        <MultistepForm steps={steps} showProgressBar={false} showNavigation={false} onComplete={handleComplete} onStepChange={handleStepChange} className="h-full px-6 py-8" />
      </FormProvider>
    </MyAccountSheet>
  );
};

export { CloseAccountSheet };
